import type { RealProfile } from '../types/profiles';
import type { RankedItem } from '../types/music';

export interface RealMusicAnalytics {
  totalPlays: number;
  topSongs: RankedItem[];
  mostPlayedSong: RankedItem | null;
  topGenres: RankedItem[];
  genreShare: { genre: string; value: number; pct: number }[];
  songsUsed: number;
  genresUsed: number;
  concentrationTop5: { plays: number; pct: number };
  concentrationTop10: { plays: number; pct: number };
  topFavorites: RankedItem[];
}

export function computeRealMusicAnalytics(profiles: RealProfile[]): RealMusicAnalytics {
  const songTotals = new Map<string, number>();
  const genreTotals = new Map<string, number>();

  for (const profile of profiles) {
    for (const mp of profile.musicPlays) {
      const key = mp.artist ? `${mp.title} — ${mp.artist}` : mp.title;
      songTotals.set(key, (songTotals.get(key) ?? 0) + mp.playCount);
    }
    for (const gp of profile.genrePlays) {
      genreTotals.set(gp.genre, (genreTotals.get(gp.genre) ?? 0) + gp.playCount);
    }
  }

  const topSongs = [...songTotals.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const topGenres = [...genreTotals.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const totalPlays = topSongs.reduce((sum, s) => sum + s.value, 0);
  const genreTotal = topGenres.reduce((sum, g) => sum + g.value, 0);

  const genreShare = topGenres.map((g) => ({
    genre: g.name,
    value: g.value,
    pct: genreTotal > 0 ? Math.round((g.value / genreTotal) * 100) : 0,
  }));

  const top5Plays = topSongs.slice(0, 5).reduce((sum, s) => sum + s.value, 0);
  const top10Plays = topSongs.slice(0, 10).reduce((sum, s) => sum + s.value, 0);

  return {
    totalPlays,
    topSongs,
    mostPlayedSong: topSongs[0] ?? null,
    topGenres,
    genreShare,
    songsUsed: songTotals.size,
    genresUsed: genreTotals.size,
    concentrationTop5: { plays: top5Plays, pct: totalPlays > 0 ? Math.round((top5Plays / totalPlays) * 100) : 0 },
    concentrationTop10: { plays: top10Plays, pct: totalPlays > 0 ? Math.round((top10Plays / totalPlays) * 100) : 0 },
    topFavorites: topSongs.slice(0, 5),
  };
}
